import { createSelector } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
// Types
import Reducers from "types/Reducers";
import QuestionTypes from "types/QuestionTypes";
// Model
import { RootState } from "store";
import IQuiz from "models/Quiz";
import IQuestion from "models/Question";
import IAnswer from "models/Answer";
// Selectors
import { selectQuestionIndex } from 'store/player/playerSelectors';
import QuizIntegrations from "types/QuizIntegrations";

dayjs.extend(isBetween);

export const selectQuiz = (state:RootState) => state[Reducers.Quiz].quiz;
export const selectQuestions = (state:RootState) => state[Reducers.Quiz].questions;
export const selectInterstitialImages = (state:RootState) => state[Reducers.Quiz].interstitialImages;
export const selectPreQuizQuestionsAnswers = (state:RootState) => state[Reducers.Quiz].preQuizQuestionsAnswers;

export const selectIsQuizStarted = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz | null) => {
    if ( !quiz ) return false;
    if ( !quiz.startsDate ) return undefined;

    const now = dayjs();
    const startDate = dayjs(Number(quiz.startsDate));
    const endDate = quiz.endsDate ? dayjs(Number(quiz.endsDate)) : '';

    return !endDate
      ? now.isAfter(startDate)
      : now.isBetween(startDate, endDate)
    ;
  }
);

export const selectIsQuizEnded = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz) => {
    if ( !quiz ) return false;
    if ( !quiz.endsDate ) return false;

    const now = dayjs();
    const endDate = dayjs(Number(quiz.endsDate));

    return now.isAfter(endDate);
  }
);

export const selectQuestionsLength = createSelector(
  [ selectQuestions ],
  (questions:IQuestion[]) => questions.length
);

export const selectQuestion = createSelector(
  [
    selectQuestions,
    selectQuestionIndex
  ],
  (questions:IQuestion[], index:number) => questions[index]
);

export const selectPostQuizQuestions = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz | null) => {
    if ( !quiz || !quiz.postQuizPages ) return {};
    return quiz.postQuizPages[0].questions.reduce((acc:any, cur:IQuestion) => {
      const question = { title: cur.title, type: cur.type };
      acc[cur.ref] = cur.type === QuestionTypes.ShortText
        ? { ...question, text: '', answerRef: uuidv4() }
        : {
            ...question,
            answers: cur.answers.reduce((a:any, c:IAnswer) => {
              a[c.ref] = { title: c.title, checked: false }
              return a;
            }, {})
          }
      ;
      return acc;
    }, {});
  }
)

export const selectPreQuizQuestions = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz | null) => {
    if ( !quiz || !quiz.preQuizPages ) return {};
    return quiz.preQuizPages[0].questions.reduce((acc:any, cur:IQuestion) => {
      const question = { title: cur.title, type: cur.type };
      acc[cur.ref] = cur.type === QuestionTypes.ShortText
        ? { ...question, text: '', answerRef: uuidv4() }
        : {
            ...question,
            answers: cur.answers.reduce((a:any, c:IAnswer) => {
              a[c.ref] = { title: c.title, checked: false }
              return a;
            }, {})
          }
      ;
      return acc;
    }, {});
  }
)

export const selectQuizIntegrationConfig = createSelector(
  [
    selectQuiz,
    (_:any, props:{ integrationName:QuizIntegrations }) => props
  ],
  (quiz: IQuiz, { integrationName }) => {
    return quiz.integrations?.find(integration => integration.service === integrationName)?.roktConfig || null
  }
);
